import { Link } from 'react-router';
import type { ProjectDTO } from '@devrobot/shared';
import { Boxes, ExternalLink, Rocket } from 'lucide-react';
import { useProjectJobs, useProjectTargets } from '../../api/queries';
import { CopyButton } from '../../components/CopyButton';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { JobsTable } from '../../components/JobsTable';
import { KeyValue } from '../../components/KeyValue';
import { Section } from '../../components/PageHeader';
import { SkeletonTable } from '../../components/Skeleton';
import { StatusBadge } from '../../components/StatusBadge';
import { TargetCard } from './TargetCard';

function Copyable({ value }: { value: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1">
      <code className="break-anywhere rounded-md bg-base-200/60 px-1.5 py-0.5 font-mono text-xs">{value}</code>
      <CopyButton value={value} label={`Copy ${value}`} />
    </span>
  );
}

export function ProjectOverviewTab({ project: p }: { project: ProjectDTO }) {
  const targets = useProjectTargets(p.key);
  const jobs = useProjectJobs(p.key);

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div className="min-w-0 space-y-6 xl:col-span-2">
        <Section
          title="Deployment targets"
          description="Each target is this project deployed to one environment on one VPS. Process, health, endpoints and observation time are shown separately."
          actions={
            p.deployable ? (
              <Link to="?tab=deploy" className="btn btn-sm">
                <Rocket className="size-4" aria-hidden="true" />
                New deployment
              </Link>
            ) : null
          }
        >
          {targets.isPending ? <SkeletonTable rows={2} /> : null}
          {targets.isError ? <ErrorAlert error={targets.error} onRetry={() => void targets.refetch()} /> : null}
          {targets.data && targets.data.length === 0 ? (
            <EmptyState icon={Boxes} title="Not deployed anywhere yet">
              {p.deployable ? 'Open the Deploy tab, pick a branch, an environment and a VPS, and review the preview before deploying.' : 'Push a branch with devrobot push first; a project becomes deployable once its repository has a branch.'}
            </EmptyState>
          ) : null}
          <div className="dr-stagger grid grid-cols-1 gap-3 lg:grid-cols-2">
            {targets.data?.map((t) => (
              <TargetCard key={t.id} t={t} />
            ))}
          </div>
        </Section>
        <Section title="Recent jobs">
          {jobs.isPending ? <SkeletonTable rows={3} /> : null}
          {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
          {jobs.data ? <JobsTable jobs={jobs.data} showProject={false} emptyText="Deployments and operations on this project appear here." /> : null}
        </Section>
      </div>
      <div className="min-w-0 space-y-6">
        <Section title="Repository">
          <KeyValue
            items={[
              {
                label: 'GitLab',
                value: p.gitlab.webUrl ? (
                  <a className="link inline-flex items-center gap-1" href={p.gitlab.webUrl} target="_blank" rel="noreferrer">
                    {p.gitlab.path} <ExternalLink className="size-3" aria-hidden="true" />
                  </a>
                ) : (
                  p.gitlab.path
                ),
              },
              { label: 'Clone', value: <Copyable value={`devrobot pull ${p.key}`} /> },
              { label: 'Default branch', value: p.defaultBranch ? <span className="font-mono">{p.defaultBranch}</span> : 'none yet' },
              { label: 'Registration', value: <StatusBadge kind="registration" value={p.registrationState} /> },
              {
                label: 'Deployable',
                value: p.deployable ? 'yes' : <span className="dr-ink-warning">no: the repository has no branch yet (run devrobot push)</span>,
              },
              { label: 'Git (HTTPS)', value: p.gitlab.httpUrl ? <Copyable value={p.gitlab.httpUrl} /> : null, hidden: !p.gitlab.httpUrl },
              { label: 'Git (SSH)', value: p.gitlab.sshUrl ? <Copyable value={p.gitlab.sshUrl} /> : null, hidden: !p.gitlab.sshUrl },
              { label: 'Your role', value: `${p.role} (from GitLab)` },
            ]}
          />
          <p className="text-xs text-base-content/60">
            <code className="font-mono">devrobot pull</code> clones with your own Git credentials and prepares local development configuration.
          </p>
        </Section>
      </div>
    </div>
  );
}
